import { Config, Designation } from "../types"
import { designationTotalCents } from "./designationTotal"

type ConfigForFeeCoverage = Pick<Config, "fee_rates">

export function calculateFeeCoverage(
  config: ConfigForFeeCoverage,
  designations: Designation[],
  paymentMethodType?: string,
): { designationsCoveringFee: Designation[]; coveredFeeCents: number } {
  const feeCents = coveredFeeCents(config, designations, paymentMethodType)

  return {
    designationsCoveringFee: distributeFeeAcrossDesignations(
      feeCents,
      designations,
    ),
    coveredFeeCents: feeCents,
  }
}

export function coveredFeeCents(
  config: ConfigForFeeCoverage,
  designations: Designation[],
  paymentMethodType?: string,
): number {
  const totalCents = designationTotalCents(designations)
  const fees = getFeeRates(config, paymentMethodType)
  const grossCents = Math.round(
    (totalCents + fees.flat_cents) / (1 - fees.percentage),
  )
  return grossCents - totalCents
}

// "card" uses card rates, everything else (including "us_bank_account") uses
// bank_account rates.
function getFeeRates(config: ConfigForFeeCoverage, paymentMethodType?: string) {
  if (!config.fee_rates || !paymentMethodType) {
    return { flat_cents: 0, percentage: 0 }
  }

  if (paymentMethodType === "card") {
    return config.fee_rates.card
  } else {
    return config.fee_rates.bank_account
  }
}

/**
 * Distributes the total fee amount (in cents) proportionally across the given
 * designations, based on their original donation amounts. Ensures the last
 * designation absorbs any remaining cents due to rounding, so the total
 * distribution always matches the input fee exactly.
 */
function distributeFeeAcrossDesignations(
  feeCents: number,
  designations: Designation[],
): Designation[] {
  if (feeCents === 0) return designations

  const totalAmount = designations.reduce(
    (sum, d) => sum + (parseFloat(d.amount) || 0),
    0,
  )
  let remainingFeeCents = feeCents

  return designations.map((designation, index) => {
    const isLastDesignation = index === designations.length - 1
    const currentAmount = parseFloat(designation.amount) || 0

    let feeToAdd = 0
    if (isLastDesignation || remainingFeeCents === 0) {
      // Last designation gets any remaining fee cents
      feeToAdd = remainingFeeCents
    } else {
      // Calculate this designation's share of the fee
      const percentOfTotal = currentAmount / totalAmount
      const feeShare = Math.round(feeCents * percentOfTotal)
      feeToAdd = Math.min(feeShare, remainingFeeCents)
    }

    remainingFeeCents -= feeToAdd

    return {
      ...designation,
      amount: (currentAmount + feeToAdd / 100).toString(),
    }
  })
}
